import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@src/reducers/store";
import {
  FirmwareUploadState,
  updateFirmwareUploadModalState,
  updateShowFirmwareUploadModal,
  updateShowOtaDownloadModal,
  updateShowOtaPushFirmwareBackupModal,
  updateShowOtaCreateBackupModal,
  updateShowOtaInstallationInProgressModal,
  updateShowOtaInstallationCompleteModal,
  updateShowOtaInstallationFailedModal,
} from "@src/reducers/your-reducer";
import { getFirmwaresUseCase } from "@src/cloud-connect/ota-update/use-cases/get-firmwares";
import { getBackupsUseCase } from "@src/cloud-connect/ota-update/use-cases/get-backups";
import { errorSnackbar } from "@src/cloud-connect/snackbars/entities/snackbar-launcher";
import {
  ActiveInstallationStatus,
  FIRMWARE_TABS,
  INSTALL_AND_ACTION_BLOCK,
} from "@src/cloud-connect/ota-update/constants";
import type { InstallAndActionBlock } from "@src/cloud-connect/ota-update/constants";
import {
  ActiveInstallationProcessPayload,
  ListItem,
  OtaDrawerDataInterface,
  OtaBackupDrawerInterface,
  TabType,
} from "@src/types/ota-update";
import { activeInstallationProcessUseCase } from "@src/cloud-connect/ota-update/use-cases/active-installation-process-use-case";
import { getInstallationProcessUseCase } from "@src/cloud-connect/ota-update/use-cases/get-installation-process-use-case";

type ActiveInstallationData = {
  data?: ActiveInstallationProcessPayload;
};

const INITIAL_VERSION_INPUT_VALUES: OtaDrawerDataInterface = {
  file: null,
  version: "",
  name: "",
  release_notes_intro: "",
  release_notes_improvements: "",
  release_notes_fixes: "",
  release_notes_general: "",
};

export const useOtaFirmware = (
  fetchCurrentFirmwareVersion: () => Promise<void>,
) => {
  const dispatch = useDispatch();

  const [modalRoot, setModalRoot] = useState<HTMLElement | null>(null);
  const [versionInputValues, setVersionInputValues] =
    useState<OtaDrawerDataInterface>(INITIAL_VERSION_INPUT_VALUES);
  const [backupInputValues, setBackupInputValues] =
    useState<OtaBackupDrawerInterface>({
      version: "",
      backup_name: "",
      backup_notes: "",
    });
  const [activeOTAHistoryTab, setActiveOTAHistoryTab] = useState<TabType>(
    FIRMWARE_TABS.VERSIONS,
  );
  const [firmwares, setFirmwares] = useState<ListItem[] | []>([]);
  const [backups, setBackups] = useState<ListItem[] | []>([]);
  const [isFirmwareLoading, setIsFirmwareLoading] = useState<boolean>(true);
  const [isBackupLoading, setIsBackupLoading] = useState<boolean>(false);
  const [selectedPushFirmwareModalRow, setSelectedPushFirmwareModalRow] =
    useState<ListItem | null>(null);
  const [
    selectedPushFirmwareBackupModalRow,
    setSelectedPushFirmwareBackupModalRow,
  ] = useState<ListItem | null>(null);
  const [showRequestForApprovalBlock, setShowRequestForApprovalBlock] =
    useState<boolean>(false);
  const [
    showPendingApprovalByOperatorBlock,
    setShowPendingApprovalByOperatorBlock,
  ] = useState<boolean>(false);
  const [showAcceptAndPushBlock, setShowAcceptAndPushBlock] =
    useState<boolean>(false);
  const [activeInstallationData, setActiveInstallationData] =
    useState<ActiveInstallationData | null>(null);
  const [installAndActionBlock, setInstallAndActionBlock] =
    useState<InstallAndActionBlock | null>(null);
  const [
    dismissedInstallationInProgressModalUpdateId,
    setDismissedInstallationInProgressModalUpdateId,
  ] = useState<string | null>(null);
  const [
    dismissedInstallationCompleteModalUpdateId,
    setDismissedInstallationCompleteModalUpdateId,
  ] = useState<string | null>(null);
  const [
    dismissedInstallationFailedModalUpdateId,
    setDismissedInstallationFailedModalUpdateId,
  ] = useState<string | null>(null);

  const authContext = useSelector(
    (state: RootState) => state.yourSlice.authContext,
  );

  const showOtaFirmwareVersionDrawerModal = useSelector(
    (state: RootState) =>
      state.yourSlice.modals.otaFirmwareVersionDrawerModal.showModal,
  );
  const showFirmwareUploadModal = useSelector(
    (state: RootState) =>
      state.yourSlice.firmwareUploadModal.showFirmwareUploadModal,
  );
  const showOtaFirmwareBackupDrawerModal = useSelector(
    (state: RootState) =>
      state.yourSlice.modals.otaFirmwareBackupDrawerModal.showModal,
  );
  const showOtaDownloadModal = useSelector(
    (state: RootState) => state.yourSlice.otaDownloadModal.showOtaDownloadModal,
  );
  const showOTAPushFirmwareModal = useSelector(
    (state: RootState) =>
      state.yourSlice.otaPushFirmwareModal.showOtaPushFirmwareModal,
  );

  const showOTACreateBackupModal = useSelector(
    (state: RootState) =>
      state.yourSlice.otaCreateBackupModal.showOtaCreateBackupModal,
  );

  const showOTAPushFirmwareBackupModal = useSelector(
    (state: RootState) =>
      state.yourSlice.otaPushFirmwareBackupModal.showOtaPushFirmwareBackupModal,
  );

  const showOTAConfirmFirmwareInstallationModal = useSelector(
    (state: RootState) =>
      state.yourSlice.otaConfirmFirmwareInstallationModal
        .showOtaConfirmFirmwareInstallationModal,
  );

  const showOTAConfirmBackupProcessModal = useSelector(
    (state: RootState) =>
      state.yourSlice.otaConfirmBackupProcessModal
        .showOtaConfirmBackupProcessModal,
  );

  const showOTAInstallationInProgressModal = useSelector(
    (state: RootState) =>
      state.yourSlice.otaInstallationInProgressModal
        .showOtaInstallationInProgressModal,
  );

  const showOTAAcceptAndPushFirmwareModal = useSelector(
    (state: RootState) =>
      state.yourSlice.otaAcceptAndPushFirmwareModal
        .showOtaAcceptAndPushFirmwareModal,
  );

  const showOTAAcceptAndCreateBackupProcessModal = useSelector(
    (state: RootState) =>
      state.yourSlice.otaAcceptAndCreateBackupProcessModal
        .showOtaAcceptAndCreateBackupProcessModal,
  );

  const showOTAInstallationCompleteModal = useSelector(
    (state: RootState) =>
      state.yourSlice.otaInstallationCompleteModal
        .showOtaInstallationCompleteModal,
  );

  const showOTAInstallationFailedModal = useSelector(
    (state: RootState) =>
      state.yourSlice.otaInstallationFailedModal.showOtaInstallationFailedModal,
  );

  const fetchFirmwares = async (signal?: AbortSignal) => {
    setIsFirmwareLoading(true);
    try {
      const result = await getFirmwaresUseCase(signal);
      if (signal?.aborted) return;
      if (result?.success && result?.data) {
        setFirmwares(result.data);
      } else {
        console.error("Error loading list:", result.error);
        errorSnackbar(result.error || "Something went wrong", 5000);
      }
    } catch (error) {
      errorSnackbar("Something went wrong", 5000);
    } finally {
      setIsFirmwareLoading(false);
    }
  };

  const fetchBackups = async () => {
    setIsBackupLoading(true);
    try {
      const result = await getBackupsUseCase();
      if (result?.success && result?.data) {
        setBackups(result.data);
      } else {
        errorSnackbar(result.error || "Something went wrong", 5000);
      }
    } catch (error) {
      errorSnackbar("Something went wrong", 5000);
    } finally {
      setIsBackupLoading(false);
    }
  };

  const closeFirmwareUploadModal = () => {
    fetchFirmwares();
    setVersionInputValues(INITIAL_VERSION_INPUT_VALUES);
    dispatch(
      updateFirmwareUploadModalState(
        FirmwareUploadState.FIRMWARE_UPLOAD_STARTED,
      ),
    );
    dispatch(updateShowFirmwareUploadModal(false));
  };

  const handleCloseOtaDownloadModal = () => {
    dispatch(updateShowOtaDownloadModal(false));
  };

  useEffect(() => {
    const el = document.getElementById("modals-entry-point");
    setModalRoot(el);
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;

    if (activeOTAHistoryTab === FIRMWARE_TABS.VERSIONS) fetchFirmwares(signal);
    if (activeOTAHistoryTab === FIRMWARE_TABS.BACKUPS) fetchBackups();

    return () => controller.abort();
  }, [activeOTAHistoryTab]);

  const isServiceTechnician =
    authContext?.permissions.includes("initiate-update-process") &&
    !authContext?.permissions.includes("approve-update-process");

  const isOperator =
    !authContext?.permissions.includes("initiate-update-process") &&
    authContext?.permissions.includes("approve-update-process");

  const isAdmin =
    authContext?.permissions.includes("initiate-update-process") &&
    authContext?.permissions.includes("approve-update-process");

  const activeInstallationStatus = activeInstallationData?.data?.status;

  useEffect(() => {
    if (
      activeInstallationStatus === ActiveInstallationStatus.PENDING_APPROVAL &&
      isAdmin
    ) {
      setInstallAndActionBlock(
        INSTALL_AND_ACTION_BLOCK.REQUEST_APPROVAL_FOR_ADMIN,
      );
    } else if (
      activeInstallationStatus === ActiveInstallationStatus.PENDING_APPROVAL &&
      isServiceTechnician
    ) {
      setInstallAndActionBlock(
        INSTALL_AND_ACTION_BLOCK.PENDING_APPROVAL_BY_OPERATOR,
      );
    } else if (
      activeInstallationStatus === ActiveInstallationStatus.PENDING_APPROVAL &&
      (isOperator || isAdmin)
    ) {
      setInstallAndActionBlock(INSTALL_AND_ACTION_BLOCK.REQUEST_FOR_APPROVAL);
    } else if (
      activeInstallationStatus === ActiveInstallationStatus.APPROVED &&
      (isServiceTechnician || isAdmin)
    ) {
      setInstallAndActionBlock(INSTALL_AND_ACTION_BLOCK.ACCEPT_AND_PUSH);
    } else if (
      activeInstallationStatus === ActiveInstallationStatus.IN_PROGRESS &&
      (isServiceTechnician || isAdmin)
    ) {
      setInstallAndActionBlock(
        INSTALL_AND_ACTION_BLOCK.INSTALLATION_IN_PROGRESS,
      );
    } else if (
      activeInstallationStatus === ActiveInstallationStatus.FAILED &&
      (isServiceTechnician || isAdmin)
    ) {
      setInstallAndActionBlock(INSTALL_AND_ACTION_BLOCK.INSTALLATION_FAILED);
    } else {
      setInstallAndActionBlock(null);
    }
  }, [
    activeInstallationData,
    activeInstallationStatus,
    authContext,
    isAdmin,
    isOperator,
    isServiceTechnician,
  ]);

  useEffect(() => {
    const updateId = activeInstallationData?.data?.update_id;
    const shouldShowInstallationInProgressModal =
      activeInstallationStatus === ActiveInstallationStatus.IN_PROGRESS &&
      (isServiceTechnician || isAdmin);

    if (!shouldShowInstallationInProgressModal) {
      dispatch(updateShowOtaInstallationInProgressModal(false));
      setDismissedInstallationInProgressModalUpdateId(null);
      return;
    }

    if (updateId && dismissedInstallationInProgressModalUpdateId === updateId) {
      dispatch(updateShowOtaInstallationInProgressModal(false));
      return;
    }

    dispatch(updateShowOtaInstallationInProgressModal(true));
  }, [
    activeInstallationData?.data?.update_id,
    activeInstallationStatus,
    dismissedInstallationInProgressModalUpdateId,
    dispatch,
    isAdmin,
    isServiceTechnician,
  ]);

  useEffect(() => {
    const updateId = activeInstallationData?.data?.update_id;
    const shouldShowInstallationCompleteModal =
      activeInstallationStatus === ActiveInstallationStatus.COMPLETED;

    if (!shouldShowInstallationCompleteModal) {
      dispatch(updateShowOtaInstallationCompleteModal(false));
      setDismissedInstallationCompleteModalUpdateId(null);
      return;
    }

    if (updateId && dismissedInstallationCompleteModalUpdateId === updateId) {
      dispatch(updateShowOtaInstallationCompleteModal(false));
      return;
    }

    dispatch(updateShowOtaInstallationCompleteModal(true));
  }, [
    activeInstallationData?.data?.update_id,
    activeInstallationStatus,
    dismissedInstallationCompleteModalUpdateId,
    dispatch,
  ]);

  useEffect(() => {
    const updateId = activeInstallationData?.data?.update_id;
    const shouldShowInstallationFailedModal =
      activeInstallationStatus === ActiveInstallationStatus.FAILED;

    if (!shouldShowInstallationFailedModal) {
      dispatch(updateShowOtaInstallationFailedModal(false));
      setDismissedInstallationFailedModalUpdateId(null);
      return;
    }

    if (updateId && dismissedInstallationFailedModalUpdateId === updateId) {
      dispatch(updateShowOtaInstallationFailedModal(false));
      return;
    }

    dispatch(updateShowOtaInstallationFailedModal(true));
  }, [
    activeInstallationData?.data?.update_id,
    activeInstallationStatus,
    dismissedInstallationFailedModalUpdateId,
    dispatch,
  ]);

  // Poll installation data immediately, then every 5 seconds.
  useEffect(() => {
    let isMounted = true;
    let isPolling = false;

    const isInstallationRunning =
      activeInstallationData?.data?.status ===
        ActiveInstallationStatus.APPROVED ||
      activeInstallationData?.data?.status ===
        ActiveInstallationStatus.IN_PROGRESS ||
      activeInstallationData?.data?.status ===
        ActiveInstallationStatus.COMPLETED ||
      activeInstallationData?.data?.status === ActiveInstallationStatus.FAILED;

    const pollInstallationData = async () => {
      if (isPolling) {
        return;
      }

      isPolling = true;

      try {
        const updateId = activeInstallationData?.data?.update_id;

        const response =
          updateId && isInstallationRunning
            ? await getInstallationProcessUseCase(updateId)
            : await activeInstallationProcessUseCase();

        if (!isMounted) {
          return;
        }

        if (response.success) {
          setActiveInstallationData(response.data || null);
        } else {
          console.error("Failed to fetch installation data:", response.error);
        }
      } finally {
        isPolling = false;
      }
    };

    pollInstallationData();

    const interval = setInterval(pollInstallationData, 2000);

    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, [
    activeInstallationData?.data?.status,
    activeInstallationData?.data?.update_id,
  ]);

  const activeList =
    activeOTAHistoryTab === FIRMWARE_TABS.VERSIONS ? firmwares : backups;
  const isLoading =
    activeOTAHistoryTab === FIRMWARE_TABS.VERSIONS
      ? isFirmwareLoading
      : isBackupLoading;
  const showingAnyBlock = installAndActionBlock !== null;

  const pushFirmwareModalData = selectedPushFirmwareModalRow ?? activeList[0];
  const pushFirmwareBackupModalData =
    selectedPushFirmwareBackupModalRow ?? activeList[0];

  const handleClosePushFirmwareModal = () => {
    setSelectedPushFirmwareModalRow(null);
  };

  const handleClosePushFirmwareBackupModal = () => {
    setSelectedPushFirmwareBackupModalRow(null);
    dispatch(updateShowOtaPushFirmwareBackupModal(false));
  };

  const handleCloseCreateBackupModal = () => {
    dispatch(updateShowOtaCreateBackupModal(false));
  };

  const handleCloseInstallationInProgressModal = () => {
    setDismissedInstallationInProgressModalUpdateId(
      activeInstallationData?.data?.update_id || null,
    );
    dispatch(updateShowOtaInstallationInProgressModal(false));
  };

  const handleCloseInstallationCompleteModal = () => {
    setDismissedInstallationCompleteModalUpdateId(
      activeInstallationData?.data?.update_id || null,
    );
    setActiveInstallationData(null);
    dispatch(updateShowOtaInstallationCompleteModal(false));
    fetchCurrentFirmwareVersion();
  };

  const handleCloseInstallationFailedModal = () => {
    setDismissedInstallationFailedModalUpdateId(
      activeInstallationData?.data?.update_id || null,
    );
    dispatch(updateShowOtaInstallationFailedModal(false));
  };

  const handleCloseOtaErrorBox = () => {
    setDismissedInstallationFailedModalUpdateId(
      activeInstallationData?.data?.update_id || null,
    );
    setActiveInstallationData(null);
    setInstallAndActionBlock(null);
    dispatch(updateShowOtaInstallationFailedModal(false));
  };

  return {
    modalRoot,
    versionInputValues,
    setVersionInputValues,
    backupInputValues,
    setBackupInputValues,
    activeOTAHistoryTab,
    setActiveOTAHistoryTab,
    firmwares,
    backups,
    activeList,
    isLoading,
    selectedPushFirmwareModalRow,
    setSelectedPushFirmwareModalRow,
    selectedPushFirmwareBackupModalRow,
    setSelectedPushFirmwareBackupModalRow,
    pushFirmwareModalData,
    pushFirmwareBackupModalData,
    showOtaFirmwareVersionDrawerModal,
    showOTAConfirmFirmwareInstallationModal,
    showOTAConfirmBackupProcessModal,
    showOTAAcceptAndPushFirmwareModal,
    showOTAAcceptAndCreateBackupProcessModal,
    showFirmwareUploadModal,
    showOtaFirmwareBackupDrawerModal,
    showOtaDownloadModal,
    fetchFirmwares,
    fetchBackups,
    closeFirmwareUploadModal,
    handleCloseOtaDownloadModal,
    setIsFirmwareLoading,
    setIsBackupLoading,
    handleClosePushFirmwareModal,
    handleClosePushFirmwareBackupModal,
    handleCloseCreateBackupModal,
    showOTAPushFirmwareModal,
    showOTACreateBackupModal,
    showOTAPushFirmwareBackupModal,
    showOTAInstallationInProgressModal,
    showOTAInstallationCompleteModal,
    showOTAInstallationFailedModal,
    showRequestForApprovalBlock,
    setShowRequestForApprovalBlock,
    showPendingApprovalByOperatorBlock,
    setShowPendingApprovalByOperatorBlock,
    showAcceptAndPushBlock,
    setShowAcceptAndPushBlock,
    activeInstallationData,
    installAndActionBlock,
    setInstallAndActionBlock,
    showingAnyBlock,
    handleCloseOtaErrorBox,
    handleCloseInstallationInProgressModal,
    handleCloseInstallationCompleteModal,
    handleCloseInstallationFailedModal,
  };
};
